<template>
  <v-dialog
    v-model="dialog"
    persistent
    transition="dialog-bottom-transition"
    max-width="850"
  >
    <!--begin::Modal dialog-->
    <div class="bg-white poppins">
      <!--begin::Modal header-->
      <div class="modal-header py-4 align-center">
        <h4 class="mb-0 font-weight-bolder">{{ headerText }}</h4>
        <!--begin::Close-->
        <div
          class="btn btn-sm btn-icon btn-active-light-info"
          @click="toggleModal"
        >
          <span class="svg-icon">
            <v-icon size="22">mdi-close</v-icon>
          </span>
        </div>
        <!--end::Close-->
      </div>
      <!--end::Modal header-->
      <!--begin::Modal body-->
      <div class="modal-body d-flex flex-column scroll-y mx-5 py-7">
        <div class="row flex-grow-0 flex-shrink-0" v-if="serverData">
          <div class="col-12 col-md-6 h-75px" v-if="!isClient">
            <v-autocomplete
              v-model="formData.customer_id"
              label="Customer"
              :items="serverData.customers"
              item-text="text"
              item-value="index"
              clearable
              outlined
              dense
              :error-messages="customerIdErrors"
              @blur="$v.formData.customer_id.$touch()"
            />
          </div>
          <div class="col-12 col-md-6 h-75px">
            <v-select
              v-model="formData.mappable_type"
              label="Type"
              :items="serverData.types"
              item-text="text"
              item-value="index"
              clearable
              outlined
              dense
              :error-messages="typeIdErrors"
              @blur="$v.formData.mappable_type.$touch()"
            />
          </div>
          <div class="col-12 col-md-6 h-75px">
            <v-select
              v-model="formData.iq_value_id"
              label="Shipping Method"
              :items="serverData.shipping_methods"
              item-text="text"
              item-value="index"
              clearable
              outlined
              dense
              :error-messages="shippingMethodIdErrors"
              @blur="$v.formData.iq_value_id.$touch()"
            />
          </div>
          <div class="col-12 col-md-6 h-75px">
            <v-text-field
              v-model="formData.mappable_value"
              label="Mappable Value"
              clearable
              outlined
              dense
              :error-messages="mappableValueErrors"
              @blur="$v.formData.mappable_value.$touch()"
            />
          </div>
        </div>

        <div
          class="my-4 min-h-75px d-flex flex-grow-1 align-end justify-center"
        >
          <button
            type="reset"
            class="btn btn-light mr-3 px-5 py-3 ls1"
            @click="resetFormData"
          >
            Clear
          </button>
          <button
            type="submit"
            class="btn btn-info px-5 py-3 ls1"
            @click="submitForm"
          >
            {{ submitButtonText }}
          </button>
        </div>
      </div>
      <!--end::Modal body-->
    </div>
    <!--end::Modal dialog-->
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
import swalAdded from "@/core/services/AlertServices/SwalAdded";
import swalEdited from "@/core/services/AlertServices/SwalEdited";
import fieldValueValidation from "@/own/mixins/fieldValueValidation.js";

export default {
  name: "ActionsDialog",
  props: ["refresher", "pageLoader"],
  mixins: [validationMixin, fieldValueValidation],
  validations: {
    formData: {
      customer_id: {
        required: requiredIf(function () {
          return !this.isClient;
        }),
      },
      mappable_type: { required },
      iq_value_id: { required },
      mappable_value: { required },
    },
  },
  data: () => ({
    dialog: false,
    serverData: null,
    formData: {
      customer_id: null,
      mappable_type: null,
      iq_value_id: null,
      mappable_value: null,
    },
  }),
  methods: {
    loadDataFromServer() {
      this.pageLoader(true);
      if (this.actionType) {
        const sendData = { id: this.$store.getters.getItemForAction.id };
        ApiService.post("/datahub/customer_wms_mappings/edit", sendData)
          .then(({ data }) => {
            this.serverData = data;
            this.formData = { ...data.customer_wms_mapping };
            this.pageLoader(false);
          })
          .catch(() => {
            this.dialog = false;
            this.pageLoader(false);
          });
      } else {
        ApiService.post("/datahub/customer_wms_mappings/create", {})
          .then(({ data }) => {
            this.serverData = data;
            this.pageLoader(false);
          })
          .catch(() => {
            this.dialog = false;
            this.pageLoader(false);
          });
      }
    },
    toggleModal() {
      if (this.dialog) {
        this.resetFormData();
      } else {
        this.loadDataFromServer();
      }
      this.dialog = !this.dialog;
    },
    handleFormValidation(fieldName) {
      const errors = [];
      if (!this.$v.formData[fieldName].$dirty) return errors;
      if ("required" in this.$v.formData[fieldName]) {
        !this.$v.formData[fieldName].required &&
          errors.push("This field is required");
      }
      return errors;
    },
    submitForm() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      const actionUrl = this.actionType
        ? "/datahub/customer_wms_mappings/update"
        : "/datahub/customer_wms_mappings/store";

      this.pageLoader(true);
      ApiService.post(actionUrl, { ...this.formData })
        .then(() => {
          if (this.actionType) swalEdited();
          else swalAdded();
          this.toggleModal();
          this.refresher();
        })
        .catch(() => {
          this.pageLoader(false);
        });
    },
    resetFormData() {
      this.formData = {
        customer_id: null,
        mappable_type: null,
        iq_value_id: null,
        mappable_value: null,
      };
    },
  },
  computed: {
    isClient: function () {
      return this.$store.getters.currentUser.data.is_client;
    },
    actionType() {
      return !!this.$store.getters.getItemForAction;
    },
    headerText() {
      return this.actionType
        ? "Edit Customer WMS Mapping"
        : "Add Customer WMS Mapping";
    },
    submitButtonText() {
      return this.actionType ? "Update" : "Submit";
    },
    customerIdErrors() {
      return this.handleFormValidation("customer_id");
    },
    typeIdErrors() {
      return this.handleFormValidation("mappable_type");
    },
    shippingMethodIdErrors() {
      return this.handleFormValidation("iq_value_id");
    },
    mappableValueErrors() {
      return this.handleFormValidation("mappable_value");
    },
  },
};
</script>
